<template>
  <div class="local-body">
    <ChromaKeyBackground>
      <div class="model-wrapper pos-rel" style="overflow: hidden">
        <!-- BroadcastBox가 토큰으로 스트림(sub)을 직접 소유·반응. 상태 아이콘도 sub별로 내부 렌더. -->
        <BroadcastBox
          ref="broadcastBox"
          @identity="onIdentity"
          :token="token"
          :sources="streamSources"
          with-outer
          :size="400"
          :always-show-status="isShowDebug"
          @stream-event="_onStreamFeed"
          @authed="_onBroadcastAuthed"
        ></BroadcastBox>
      </div>
      <!-- 모델 아래: SESSION 룰 키워드(펫이 이 세션에 흡수한 것) 뇌구조 워드클라우드 (dev 전용) -->
      <div v-if="isShowDebug" class="session-brain">
        <div class="session-brain__head">
          <v-switch
            v-model="autoReloadSessionRule"
            dense
            hide-details
            color="primary"
            class="ma-0 pa-0"
            label="세션 룰 자동 생성"
          ></v-switch>
          <v-btn x-small depressed color="primary" :loading="sessionGenerating" @click="_generateSessionRules">
            지금 생성
          </v-btn>
        </div>
        <BrainWordCloud :words="sessionKeywords"></BrainWordCloud>
      </div>
    </ChromaKeyBackground>
    <!-- 모델 우측 채팅 피드 (dev 전용): 각 소스 이벤트를 시간순 표시 -->
    <ChatFeedPanel
      v-if="isShowDebug"
      :items="feed"
      @clear="feed = []"
      @chat="_debugChat"
      @donation="_debugDonation"
      @exp="_debugExp"
    ></ChatFeedPanel>
    <!-- 채팅 피드 왼쪽 라이브 분석 패널 (dev 전용): 렌즈 A·C·D 랭크 -->
    <AnalysisPanel v-if="isShowDebug" :analysis="analysis" :updated-at="analysisUpdatedAt"></AnalysisPanel>
  </div>
</template>

<script>
import ChatFeedPanel from "./fragments/ChatFeedPanel.vue";
import AnalysisPanel from "./fragments/AnalysisPanel.vue";
import BrainWordCloud from "@/views/Lab/BrainWordCloud.vue";
import { flushChatLog, beaconChatLog, analyzeSession, generateSessionRules } from "@/api/RndApi";
import SupabaseApi from "@/api/SupabaseApi";
import { SessionRuleTrigger, coldThenSteady } from "@/domain/session/SessionRuleTrigger";
import { rulesToWeightedWords } from "@/domain/session/sessionKeywords";

// 채팅 로그 적재: 실 플랫폼만, 쌓아서 배치 전송(건건 X → 오버헤드 방지)
const CHAT_LOG_PLATFORMS = ["chzzk", "twitch", "chzzk_relay"];
const CHAT_LOG_FLUSH_MS = 5000;
const CHAT_LOG_FLUSH_SIZE = 50;
const CHAT_LOG_BUFFER_CAP = 500; // 전송 실패 누적 상한(메모리 보호)

// SESSION 키워드 자동 갱신: 신규 메시지가 N개 쌓일 때마다 rnd에서 재생성 → BroadcastBox._sessionRules 교체.
// 매 틱 폴링이 아니라 메시지 이벤트 기반. 페이지 인스턴스별로 독립(탭 간 공유 안 됨).
const AUTO_RELOAD_SESSION_RULE = true; // 초기값(dev 스위치로 런타임 토글)
// 재생성 정책: 캐시 없으면 first개로 빠른 첫 생성, 이후엔 steady개 AND steadyMs 경과.
// 캐시(마지막 세션룰) 있으면 워밍 스타트 후 바로 steady.
const SESSION_FIRST_MSG = 100; // 캐시 없을 때 첫 생성 임계
const SESSION_STEADY_MSG = 1000; // 이후 신규 메시지 임계
const SESSION_STEADY_MS = 10 * 60 * 1000; // 이후 경과 시간(10분)

// 라이브 분석(dev 전용): 주기적으로 rnd /analyze/session → 서버가 오늘 jsonl 전체 읽어 렌즈 A·C·D
// (생성과 동일 소스 → 리프레시해도 하루 누적 유지)
const ANALYZE_MS = 8000;
const ANALYZE_TOP = 15;

// OBS 오버레이 페이지. 스트림 구독·신원·데이터·반응은 전부 BroadcastBox가 소유한다(자족형).
// 이 페이지는 (1) URL 토큰 전달 (2) /on-air/dev 디버그 패널만 담당.
export default {
  components: { ChatFeedPanel, AnalysisPanel, BrainWordCloud },
  data() {
    const isShowDebug = this.$route.name === "OnAirDev"; // /on-air/dev에서만 디버그. /on-air(송출)엔 절대 노출 안 함.
    const token = this.$route.query.t;
    return {
      isShowDebug,
      token,
      // 토큰 있을 때만 구독. virtupet(제어)+chat 후보(chzzk/twitch/chzzk_relay) — BroadcastBox가 설정된 것만 실제 오픈.
      // chzzk_relay = 관리자 relay(남의 채널 읽기, 게이트웨이 경유).
      streamSources: token ? ["virtupet", "chzzk", "twitch", "chzzk_relay"] : [],
      feed: [], // 채팅 피드 (각 소스 시청자 이벤트). dev 우측 패널 표시용.
      // ⚠️ Vue2는 _/$ 로 시작하는 data 키를 this 에 프록시하지 않음 → this._x 는 undefined.
      //    그래서 언더스코어 없이 feedSeq 로 둬야 this.feedSeq 가 동작한다.
      feedSeq: 0,
      chatLogBuffer: [], // 적재 대기 버퍼 (쌓아서 flush)
      chatLogSending: false,
      sessionOwnerId: null, // 오버레이가 신원 직접 전달(B방식)
      sessionPetId: null,
      sessionChannel: null, // SESSION 룰 생성용 채널명(첫 이벤트에서 캡처)
      sessionPlatform: null, // SESSION 룰 생성용 플랫폼(provider)
      autoReloadSessionRule: AUTO_RELOAD_SESSION_RULE, // dev 스위치로 토글
      sessionWarmStarted: false, // 첫 접근 시 캐시(마지막 세션룰) 워밍 스타트 1회
      sessionGenerating: false, // 생성 중복 방지
      sessionKeywords: [], // 생성된 SESSION 룰의 키워드 union(뇌구조 워드클라우드용)
      analysis: { A: [], C: [], D: [], count: 0 }, // 라이브 렌즈 A·C·D 랭크
      analysisUpdatedAt: 0, // 마지막 분석 성공 시각(ms)
      analyzing: false,
    };
  },
  created() {
    // 세션 룰 재생성 타이밍 판단(캡슐화). 정책 교체로 로직 변경 가능. 비반응 인메모리.
    this.sessionTrigger = new SessionRuleTrigger(
      coldThenSteady({ first: SESSION_FIRST_MSG, steadyCount: SESSION_STEADY_MSG, steadyMs: SESSION_STEADY_MS }),
    );
  },
  mounted() {
    if (!this.token) {
      this.$notify({
        type: "error",
        title: "잘못된 접근입니다",
        text: "OBS 링크가 유효하지 않습니다. 제어판에서 링크를 다시 발급해주세요.",
        duration: 3000,
      });
      return;
    }
    // 채팅 로그: 주기 플러시 + 언로드(탭 닫기/이동) 시 beacon 플러시
    this.chatLogTimer = setInterval(this._flushChatLog, CHAT_LOG_FLUSH_MS);
    this.onHideHandler = () => {
      if (document.visibilityState === "hidden") this._beaconFlushChatLog();
    };
    this.onPageHideHandler = () => this._beaconFlushChatLog();
    document.addEventListener("visibilitychange", this.onHideHandler);
    window.addEventListener("pagehide", this.onPageHideHandler);

    // 라이브 분석은 dev(디버그 패널)에서만
    if (this.isShowDebug) this.analyzeTimer = setInterval(this._runAnalyze, ANALYZE_MS);
  },
  beforeDestroy() {
    if (this.chatLogTimer) clearInterval(this.chatLogTimer);
    if (this.analyzeTimer) clearInterval(this.analyzeTimer);
    if (this.onHideHandler) document.removeEventListener("visibilitychange", this.onHideHandler);
    if (this.onPageHideHandler) window.removeEventListener("pagehide", this.onPageHideHandler);
    this._beaconFlushChatLog();
  },
  methods: {
    // 브라우저 탭 제목: 버츄펫 | {스트리머} 의 {펫}
    onIdentity({ streamerName, petName }) {
      if (streamerName && petName) document.title = `버츄펫 | ${streamerName}의 ${petName}`;
      else if (petName) document.title = `버츄펫 | ${petName}`;
      else document.title = "버츄펫";
    },
    // 디버그(/on-air/dev): BroadcastBox 인터프리터에 StreamEvent 직접 주입 (실스트림 없이 반응 테스트)
    _debugChat(chat) {
      this.$refs.broadcastBox.inject({ type: "CHAT", ...chat });
    },
    _debugDonation(donation) {
      this.$refs.broadcastBox.inject({ type: "DONATION", ...donation });
    },
    _debugExp(value) {
      // exp 증가는 BroadcastBox의 levelExpDataMixin이 소유 → ref로 호출
      if (this.$refs.broadcastBox && this.$refs.broadcastBox.increaseExp) {
        this.$refs.broadcastBox.increaseExp(value);
      }
    },
    // BroadcastBox가 올린 시청자 이벤트를 피드에 누적(최근 200개 유지).
    _onStreamFeed(item) {
      this.feedSeq += 1;
      // id는 스프레드 뒤에 둬서 item 자체의 id가 덮어쓰지 못하게 — v-for key 중복 방지
      this.feed.push({ ...item, id: this.feedSeq });
      if (this.feed.length > 200) this.feed.splice(0, this.feed.length - 200);
      this._bufferChatLog(item);
    },

    // ───────── 채팅 로그 적재 (rnd /chat-log 로 배치 전송) ─────────
    _bufferChatLog(item) {
      if (!this.token || !item || !CHAT_LOG_PLATFORMS.includes(item.provider)) return;
      // 신원(owner/pet)은 오버레이가 직접 전달(B방식) — 첫 이벤트에서 캡처
      if (!this.sessionOwnerId && item.ownerId) this.sessionOwnerId = item.ownerId;
      if (!this.sessionPetId && item.petId) this.sessionPetId = item.petId;
      // SESSION 룰 생성용 신원(채널/플랫폼)도 첫 이벤트에서 캡처
      if (!this.sessionChannel && item.channelName) this.sessionChannel = item.channelName;
      if (!this.sessionPlatform && item.provider) this.sessionPlatform = item.provider;
      const ready = this.sessionOwnerId && this.sessionChannel && this.sessionPlatform;
      // 워밍 스타트 폴백: auth에서 신원을 못 받았을 때(주채널 없음 등) 첫 채팅으로 캐시 적용. 스위치 무관.
      if (ready && !this.sessionWarmStarted) {
        this.sessionWarmStarted = true;
        this._warmStartSession();
      }
      // SESSION 키워드 자동 갱신 (스위치 on일 때만). live·dev 둘 다. 타이밍 판단은 sessionTrigger가 캡슐화.
      if (this.autoReloadSessionRule && item.message && ready) {
        if (this.sessionTrigger.onMessage()) this._generateSessionRules();
      }
      this.chatLogBuffer.push({
        provider: item.provider,
        type: item.type,
        nickname: item.nickname,
        message: item.message,
        amount: item.amount,
        channelName: item.channelName, // 네임스페이스(owner/채널/platform)용
        raw: item.raw,
      });
      if (this.chatLogBuffer.length > CHAT_LOG_BUFFER_CAP) {
        this.chatLogBuffer.splice(0, this.chatLogBuffer.length - CHAT_LOG_BUFFER_CAP);
      }
      if (this.chatLogBuffer.length >= CHAT_LOG_FLUSH_SIZE) this._flushChatLog();
    },
    _chatLogPayload(events) {
      return { owner_id: this.sessionOwnerId, pet_id: this.sessionPetId, events };
    },
    async _flushChatLog() {
      if (this.chatLogSending || !this.token || !this.chatLogBuffer.length) return;
      const batch = this.chatLogBuffer.splice(0, CHAT_LOG_FLUSH_SIZE);
      this.chatLogSending = true;
      const ok = await flushChatLog(this._chatLogPayload(batch));
      this.chatLogSending = false;
      // 실패 시 앞에 되돌려 다음 기회에 재시도(cap 한도 내)
      if (!ok) this.chatLogBuffer = batch.concat(this.chatLogBuffer).slice(0, CHAT_LOG_BUFFER_CAP);
    },
    _beaconFlushChatLog() {
      if (!this.token || !this.chatLogBuffer.length) return;
      const batch = this.chatLogBuffer.splice(0, this.chatLogBuffer.length);
      beaconChatLog(this._chatLogPayload(batch));
    },

    // ───────── 라이브 분석 (rnd /analyze/session → 서버가 오늘 jsonl 전체 렌즈) ─────────
    async _runAnalyze() {
      if (this.analyzing) return;
      if (!this.sessionOwnerId || !this.sessionChannel || !this.sessionPlatform) return;
      this.analyzing = true;
      const res = await analyzeSession({
        ownerId: this.sessionOwnerId,
        channel: this.sessionChannel,
        platform: this.sessionPlatform,
        top: ANALYZE_TOP,
      });
      this.analyzing = false;
      if (res) {
        this.analysis = res;
        this.analysisUpdatedAt = Date.now();
      }
    },

    // BroadcastBox 인증 완료 → 토큰으로 푼 신원을 마운트 시점에 확보(첫 채팅 대기 X).
    _onBroadcastAuthed({ ownerId, petId, channel, platform } = {}) {
      if (ownerId) this.sessionOwnerId = ownerId;
      if (petId) this.sessionPetId = petId;
      if (channel) this.sessionChannel = channel;
      if (platform) this.sessionPlatform = platform;
      // 신원 확보되면 즉시 캐시 워밍 스타트(스위치와 무관 — 지난 세션룰 표시/적용)
      if (!this.sessionWarmStarted && this.sessionOwnerId && this.sessionChannel) {
        this.sessionWarmStarted = true;
        this._warmStartSession();
      }
    },

    // rules(빈도 순위 정렬됨) → 뇌 워드클라우드용 {word, weight}. 공유 유틸(BroadcastBox와 동일).
    _keywordsFromRules(rules) {
      return rulesToWeightedWords(rules);
    },

    // ───────── SESSION 워밍 스타트 (DB의 마지막 세션룰 먼저 적용) ─────────
    async _warmStartSession() {
      const rules = await SupabaseApi.ReactionKeywordSessionRule.fetchLatest(this.sessionOwnerId);
      if (rules && rules.length && this.$refs.broadcastBox) {
        this.$refs.broadcastBox.setSessionRules(rules);
        this.sessionKeywords = this._keywordsFromRules(rules);
        // 캐시 있음 → "방금 생성한 셈"으로 씨딩 → 빠른-first 건너뛰고 바로 steady(1000 && 10분)
        this.sessionTrigger.markGenerated();
        if (this.isShowDebug) console.log("[session] warm-start ←", rules.length);
      } else if (this.isShowDebug) {
        console.log("[session] no cache → cold (first", SESSION_FIRST_MSG, ")");
      }
    },

    // ───────── SESSION 키워드 룰 생성 (rnd /rules/generate → BroadcastBox 주입) ─────────
    async _generateSessionRules() {
      if (this.sessionGenerating) return;
      if (!this.sessionOwnerId || !this.sessionChannel || !this.sessionPlatform) return;
      this.sessionGenerating = true;
      if (this.isShowDebug) console.log("[session] generate →", this.sessionChannel, this.sessionPlatform);
      const rules = await generateSessionRules({
        ownerId: this.sessionOwnerId,
        channel: this.sessionChannel,
        platform: this.sessionPlatform,
      });
      this.sessionGenerating = false;
      if (this.isShowDebug) console.log("[session] rules ←", rules ? rules.length : "null");
      // 별도 SESSION 레이어로 주입(_keywordRules와 무관). 실패(null)면 기존 유지.
      if (rules && this.$refs.broadcastBox) {
        this.$refs.broadcastBox.setSessionRules(rules);
        // 뇌구조 워드클라우드: 룰 키워드 전부 union(중복 제거)
        this.sessionKeywords = this._keywordsFromRules(rules);
      }
    },
  },
};
</script>

<style scoped>
.w-100 {
  width: 100%;
}

.h-100 {
  height: 100%;
}

.local-body {
  width: 100vw;
  height: 100vh;
}

.model-wrapper {
  width: fit-content;
}

/* 모델 아래 뇌구조 워드클라우드 (dev 전용) */
.session-brain {
  width: 420px;
  margin-top: 8px;
}
.session-brain__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.session-brain >>> .brain-chart {
  height: 360px;
}
</style>
